import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { FeatureFlagsServiceActions } from './feature-flags.actions';
import { FeatureFlagsService } from '../../core/services/feature-flags.service';
import { LoggingService } from '../../core/services/logging.service';
import {
  getAllFlagKeys,
  getFlagConfig,
} from '../../core/constants/feature-flags.constants';
import { toFeatureFlags } from '../../core/models/feature-flags.model';

/**
 * Feature Flags Effects
 * Handles side effects for feature flags state management
 */
@Injectable()
export class FeatureFlagsEffects {
  private readonly actions$ = inject(Actions);
  private readonly featureFlagsService = inject(FeatureFlagsService);
  private readonly loggingService = inject(LoggingService);

  /**
   * Initialize all feature flag values when LaunchDarkly client is ready
   * Bulk updates all flags in a single action for performance
   */
  initializeFeatureToggles$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FeatureFlagsServiceActions.ready),
      map(() => {
        const flagKeys = getAllFlagKeys();

        // Build complete FeatureFlags object with dynamic domain discovery
        const allFlags: Record<string, Record<string, unknown>> = {};

        // Collect all flag values at once
        for (const key of flagKeys) {
          const config = getFlagConfig(key);
          const toggleValue = this.featureFlagsService.getToggleValue(
            key,
            config.defaultValue
          );

          this.loggingService.debug(
            `[FeatureFlagsEffects] Initializing flag: ${key}`,
            { key, value: toggleValue }
          );

          // Auto-create domains as needed
          if (!allFlags[config.domain]) {
            allFlags[config.domain] = {};
          }
          allFlags[config.domain][config.property] = toggleValue;
        }

        // Single bulk update action with type-safe conversion
        return FeatureFlagsServiceActions.bulkUpdate({
          flags: toFeatureFlags(allFlags),
        });
      }),
      catchError(error => {
        // Use structured logging instead of console.error
        this.loggingService.error(
          '[FeatureFlagsEffects] Error initializing flags',
          error instanceof Error ? error : undefined,
          error instanceof Error ? undefined : { errorValue: String(error) }
        );
        // Return an init failed action to maintain the stream
        return of(
          FeatureFlagsServiceActions.initFailed({
            error:
              error instanceof Error
                ? error.message
                : 'Unknown error during flag initialization',
          })
        );
      })
    );
  });
}
